import { useCallback, useState } from "react";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import {
  initialControleRecebimentoEpiListaState,
  useControleRecebimentoEpiListaFilters,
} from "../hooks/ControleRecebimentoEpi_lista/filters";
import { useControleRecebimentoEpiListaPagination } from "../hooks/ControleRecebimentoEpi_lista/pagination";
import { GestaoRecebimentoEpi } from "../../domain/models/gestao_recebimento_epi";
import { GestaoEpiDropdown } from "../../domain/models/gestao_epi_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { FornecedorDropDown } from "../../../../common/domain/models/fornecedor_dropdown";
import {
  GestaoRecebimentoEpiForm,
  GestaoRecebimentoEpiSchema,
} from "../schemas/ControleRecebimentoEpi_lista/gestao_recebimento_epi";
import {
  useCreateDebounceHandler,
  useDebouncedCallback,
} from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

/**
 * LoadGestaoRecebimentoEpiListaFuncState
 */

type LoadGestaoRecebimentoEpiListaFuncState = {
  idsGestaoEpi?: number[] | null;
  idsFornecedores?: number[] | null;
  ca?: string | null;
  dataRecebimento?: (Date | null)[] | null;
  dataValidade?: (Date | null)[] | null;
  page?: number;
  rowsPerPage?: number;
};

export const useControleRecebimentoEpiListaViewModel = () => {
  const {
    commons: { getCentroCustoDropDownUseCase },
    gestaoEpi: {
      getAllGestaoRecebimentoEpiUseCase,
      getGestaoEpiDropDownUseCase,
      createGestaoRecebimentoEpiUseCase,
      updateGestaoRecebimentoEpiUseCase,
      deleteGestaoRecebimentoEpiUseCase,
      getArquivoNotaFiscalUseCase
    },
  } = useDependencyInjection();

  const { pushInfoNotification, pushErrorNotification, setCustomLoading } =
    usePageLoader();

  const { loadFornecedoresDropDown } = useDropDownLoader();

  const debouncedHandler = useCreateDebounceHandler(750);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [gestaoEpiDropdown, setGestaoEpiDropdown] =
    useState<GestaoEpiDropdown[]>();
  const [fornecedoresDropdown, setFornecedoresDropdown] =
    useState<FornecedorDropDown[]>();
  const [centroCustoDropdown, setCentroCustoDropdown] =
    useState<CentroCustoDropDown[]>();

  const [
    gestaoRecebimentoEpiCadastroModal,
    setGestaoRecebimentoEpiCadastroModal,
  ] = useState(false);
  const [gestaoRecebimentoEpiDeleteModal, setGestaoRecebimentoEpiDeleteModal] =
    useState(false);

  const [gestaoRecebimentoEpiLista, setGestaoRecebimentoEpiLista] =
    useState<GestaoRecebimentoEpi[]>();
  const [selectedRecebimentoEpi, setSelectedGestaoRecebimentoEpi] =
    useState<GestaoRecebimentoEpi>();

  const [notaFiscalDeleteModal, setNotaFiscalDeleteModal] =
    useState<boolean>(false);
  const [openDeleteArquivoModal, setOpenDeleteArquivoModal] =
    useState<boolean>(false);
  const [limparArquivo, setLimparArquivo] = useState<string>();
  const [arquivo, setArquivo] = useState<string>();

  const filters = useControleRecebimentoEpiListaFilters();
  const pagination = useControleRecebimentoEpiListaPagination();

  const hookForm = useForm<GestaoRecebimentoEpiForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GestaoRecebimentoEpiSchema),
  });

  /**
   * Funcoes de carregamento
   */

  const loadGestaoEpiDropdown = useCallback(async () => {
    try {
      const results = await getGestaoEpiDropDownUseCase.getGestaoEpiDropDown();
      setGestaoEpiDropdown(results);
    } catch (e: unknown) {
      setGestaoEpiDropdown(undefined);
      throw new Error("Falha ao buscar lista de epis!");
    }
  }, [getGestaoEpiDropDownUseCase]);
  const loadCentroCustoDropdown = useCallback(async () => {
    try {
      const centroCusto =
        await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setCentroCustoDropdown(centroCusto);
    } catch (e: unknown) {
      throw e;
    }
  }, [getCentroCustoDropDownUseCase]);

  const loadGestaoRecebimentoEpiLista = useCallback(
    async (fs?: LoadGestaoRecebimentoEpiListaFuncState) => {
      try {
        const filteredIdsGestaoEpi = evaluateParamValue(
          fs?.idsGestaoEpi,
          filters.idsGestaoEpi,
        );
        const filteredIdsFornecedores = evaluateParamValue(
          fs?.idsFornecedores,
          filters.idsFornecedores,
        );
        const filteredCa = evaluateParamValue(fs?.ca, filters.ca);
        const filteredDataRecebimento = evaluateParamValue(
          fs?.dataRecebimento,
          filters.dataRecebimento,
        );
        const filteredDataValidade = evaluateParamValue(
          fs?.dataValidade,
          filters.dataValidade,
        );

        const page = fs?.page ?? pagination.page;
        const rowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllGestaoRecebimentoEpiUseCase.getAllGestaoRecebimentoEpi(
          filteredIdsGestaoEpi,
          filteredIdsFornecedores,
          filteredCa,
          filteredDataRecebimento,
          filteredDataValidade,
          page,
          rowsPerPage,
        );
        setGestaoRecebimentoEpiLista(getAllGestaoRecebimentoEpiUseCase.items);
        pagination.setTotalItems(getAllGestaoRecebimentoEpiUseCase.totalItems);
      } catch (e: unknown) {
        setGestaoRecebimentoEpiLista(undefined);
        throw new Error("Falha ao buscar lista de epis!");
      }
    },
    [filters, pagination, getAllGestaoRecebimentoEpiUseCase],
  );

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadFornecedoresDropDown((dt) => setFornecedoresDropdown(dt));
      await loadGestaoEpiDropdown();
      await loadCentroCustoDropdown();
      await loadGestaoRecebimentoEpiLista();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadFornecedoresDropDown((dt) => setFornecedoresDropdown(dt));
      await loadGestaoEpiDropdown();
      await loadCentroCustoDropdown();
      await loadGestaoRecebimentoEpiLista();
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao recarregar pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [
    loadCentroCustoDropdown,
    loadFornecedoresDropDown,
    loadGestaoEpiDropdown,
    loadGestaoRecebimentoEpiLista,
    pushErrorNotification,
  ]);

  const handleCadastroGestaoRecebimentoEpiModalOpenState = useCallback(
    (idGestaoRecebimentoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (gestaoRecebimentoEpiLista && idGestaoRecebimentoEpi) {
          setSelectedGestaoRecebimentoEpi(
            gestaoRecebimentoEpiLista.find(
              (x) => x.idGestaoRecebimentoEpi === idGestaoRecebimentoEpi,
            ),
          );
        } else {
          setSelectedGestaoRecebimentoEpi(undefined);
        }
      }
      setGestaoRecebimentoEpiCadastroModal(openState);
    },
    [gestaoRecebimentoEpiLista],
  );

  const handleCadastroGestaoRecebimentoEpiSubmit = useCallback(
    async (form: GestaoRecebimentoEpiForm) => {
      try {
        setSaving(true);
        if (selectedRecebimentoEpi) {
          await updateGestaoRecebimentoEpiUseCase.updateGestaoRecebimentoEpi(
            selectedRecebimentoEpi.idGestaoRecebimentoEpi,
            form.idGestaoEpi,
            form.idFornecedor,
            form.ca,
            form.quantidade,
            form.dataRecebimento,
            form.dataValidade,
            form.idCentroCusto,
            form.marca,
            form.notaFiscal,
            form.limparArquivoNotaFiscal, 
            form.ArquivoNotaFiscal ?? null,
            form.ValorUnitario,
            form.ValorTotal
          );
        } else {
          await createGestaoRecebimentoEpiUseCase.createGestaoRecebimentoEpi(
            form.idGestaoEpi,
            form.idFornecedor,
            form.ca,
            form.quantidade,
            form.dataRecebimento,
            form.dataValidade,
            form.idCentroCusto,
            form.marca,
            form.notaFiscal,
            form.ArquivoNotaFiscal ?? null,
            form.ValorUnitario,
            form.ValorTotal
          );
        }
        setGestaoRecebimentoEpiCadastroModal(false);
        setSelectedGestaoRecebimentoEpi(undefined);
        await loadGestaoRecebimentoEpiLista();
        pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao salvar recebimento de epi!"),
        );
      } finally {
        setSaving(false);
      }
    },
    [
      selectedRecebimentoEpi,
      createGestaoRecebimentoEpiUseCase,
      updateGestaoRecebimentoEpiUseCase,
      loadGestaoRecebimentoEpiLista,
      pushInfoNotification,
      pushErrorNotification,
    ],
  );

  const handleDeleteGestaoEpiModalOpenState = useCallback(
    (idGestaoRecebimentoEpi: number | null, openState: boolean) => {
      if (openState) {
        if (gestaoRecebimentoEpiLista && idGestaoRecebimentoEpi) {
          setSelectedGestaoRecebimentoEpi(
            gestaoRecebimentoEpiLista.find(
              (x) => x.idGestaoRecebimentoEpi === idGestaoRecebimentoEpi,
            ),
          );
        } else {
          setSelectedGestaoRecebimentoEpi(undefined);
        }
      }
      setGestaoRecebimentoEpiDeleteModal(openState);
    },
    [gestaoRecebimentoEpiLista],
  );

  const handleDeleteGestaoEpiSubmit = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedRecebimentoEpi) {
        await deleteGestaoRecebimentoEpiUseCase.deleteGestaoRecebimentoEpi(
          selectedRecebimentoEpi.idGestaoRecebimentoEpi,
        );
      }
      setGestaoRecebimentoEpiDeleteModal(false);
      setSelectedGestaoRecebimentoEpi(undefined);
      await loadGestaoRecebimentoEpiLista();
      pushInfoNotification("Sucesso", "Registro salvo com sucesso!");
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao excluir recebimento de epi!"),
      );
    } finally {
      setSaving(false);
    }
  }, [
    selectedRecebimentoEpi,
    deleteGestaoRecebimentoEpiUseCase,
    loadGestaoRecebimentoEpiLista,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useControleRecebimentoEpiListaPagination.setState({ page: page + 1 });
        await loadGestaoRecebimentoEpiLista({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useControleRecebimentoEpiListaPagination.setState({
          rowsPerPage,
          page: 1,
        });
        await loadGestaoRecebimentoEpiLista({ rowsPerPage, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao recarregar pagina!"),
        );
      } finally {
        setLoading(false);
      }
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleGestaoEpiChange = useDebouncedCallback(
    debouncedHandler,
    async (ids: number[] | null) => {
      useControleRecebimentoEpiListaFilters.setState({ idsGestaoEpi: ids });
      return async () => {
        try {
          setLoading(true);
          useControleRecebimentoEpiListaPagination.setState({ page: 1 });
          await loadGestaoRecebimentoEpiLista({ idsGestaoEpi: ids, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleFornecedoresChange = useDebouncedCallback(
    debouncedHandler,
    async (ids: number[] | null) => {
      useControleRecebimentoEpiListaFilters.setState({ idsFornecedores: ids });
      return async () => {
        try {
          setLoading(true);
          useControleRecebimentoEpiListaPagination.setState({ page: 1 });
          await loadGestaoRecebimentoEpiLista({
            idsFornecedores: ids,
            page: 1,
          });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleCaChange = useDebouncedCallback(
    debouncedHandler,
    async (ca: string | null) => {
      useControleRecebimentoEpiListaFilters.setState({ ca });
      return async () => {
        try {
          setLoading(true);
          useControleRecebimentoEpiListaPagination.setState({ page: 1 });
          await loadGestaoRecebimentoEpiLista({ ca, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleDataRecebimentoChange = useDebouncedCallback(
    debouncedHandler,
    async (dataRecebimento: (Date | null)[] | null) => {
      useControleRecebimentoEpiListaFilters.setState({ dataRecebimento });
      return async () => {
        try {
          setLoading(true);
          useControleRecebimentoEpiListaPagination.setState({ page: 1 });
          await loadGestaoRecebimentoEpiLista({ dataRecebimento, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleDataValidadeChange = useDebouncedCallback(
    debouncedHandler,
    async (dataValidade: (Date | null)[] | null) => {
      useControleRecebimentoEpiListaFilters.setState({ dataValidade });
      return async () => {
        try {
          setLoading(true);
          useControleRecebimentoEpiListaPagination.setState({ page: 1 });
          await loadGestaoRecebimentoEpiLista({ dataValidade, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification(
            "Erro",
            processError(e, "Falha ao buscar dados da pagina!"),
          );
        } finally {
          setLoading(false);
        }
      };
    },
    [loadGestaoRecebimentoEpiLista, pushErrorNotification],
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useControleRecebimentoEpiListaFilters.setState({
        ...initialControleRecebimentoEpiListaState,
      });
      useControleRecebimentoEpiListaPagination.setState({ page: 1 });
      await loadGestaoRecebimentoEpiLista({
        ...initialControleRecebimentoEpiListaState,
        page: 1,
      });
    } catch (e: unknown) {
      pushErrorNotification(
        "Erro",
        processError(e, "Falha ao buscar dados da pagina!"),
      );
    } finally {
      setLoading(false);
    }
  }, [loadGestaoRecebimentoEpiLista, pushErrorNotification]);

  //#region ARQUIVOS

  const handleDownloadFile = useCallback(async () => {
    if (selectedRecebimentoEpi?.idGestaoRecebimentoEpi) {
      try {
        setCustomLoading(true);

        await getArquivoNotaFiscalUseCase.getArquivoNotaFiscal(
          selectedRecebimentoEpi?.idGestaoRecebimentoEpi,
          selectedRecebimentoEpi.nomeNotaFiscal ?? "",
        );
      } catch (e: unknown) {
        pushErrorNotification(
          "Erro",
          processError(e, "Falha ao fazer download do arquivo!"),
          { isAutoFinish: true },
        );
      } finally {
        setCustomLoading(false);
      }
    }
  }, [getArquivoNotaFiscalUseCase, pushErrorNotification, selectedRecebimentoEpi?.idGestaoRecebimentoEpi, selectedRecebimentoEpi?.nomeNotaFiscal, setCustomLoading]);

  const handleArquivoDeleteOpenState = useCallback(
    (
      limparArquivos: string | null,
      arquivo: string | null,
      openState: boolean,
    ) => {
      if (openState && limparArquivos && arquivo) {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(limparArquivos);
        setArquivo(arquivo);
      } else {
        setOpenDeleteArquivoModal(openState);
        setLimparArquivo(undefined);
        setArquivo(undefined);
      }
    },
    [],
  );

  const handleFileChange = useCallback(
    (fileId: number | null) => {
      if (fileId && selectedRecebimentoEpi) {
        switch (fileId) {
          case 1:
            setSelectedGestaoRecebimentoEpi({
              ...selectedRecebimentoEpi,
              nomeNotaFiscal: "",
            });
            hookForm.setValue("limparArquivoNotaFiscal", true);
            break;
        }
      }
    },
    [selectedRecebimentoEpi, hookForm],
  );

  // const handleConfirmarDeletarArquivo = useCallback(async () => {
  //   if (limparArquivo && arquivo) {
  //       handleDeleteFile(limparArquivo, arquivo)
  //       setOpenDeleteArquivoModal(false)
  //       setArquivo(undefined)
  //       setLimparArquivo(undefined)
  //   }
  // }, [limparArquivo, arquivo, handleDeleteFile]);

  //#endregion

  return {
    loading,
    saving,
    errorMessage,
    gestaoRecebimentoEpiCadastroModal,
    gestaoRecebimentoEpiDeleteModal,
    gestaoEpiDropdown: gestaoEpiDropdown ?? [],
    fornecedoresDropdown: fornecedoresDropdown ?? [],
    gestaoRecebimentoEpiLista: gestaoRecebimentoEpiLista ?? [],
    centroCustoDropdown: centroCustoDropdown ?? [],
    selectedRecebimentoEpi: selectedRecebimentoEpi ?? null,
    filters,
    pagination,
    handleReload,
    handleCadastroGestaoRecebimentoEpiModalOpenState,
    handleCadastroGestaoRecebimentoEpiSubmit,
    handleDeleteGestaoEpiModalOpenState,
    handleDeleteGestaoEpiSubmit,
    handlePageChange,
    handleRowsPerPageChange,
    handleGestaoEpiChange,
    handleFornecedoresChange,
    handleCaChange,
    handleDataRecebimentoChange,
    handleDataValidadeChange,
    handleClearFilters,
    handleDownloadFile,
    handleArquivoDeleteOpenState,
    handleFileChange,
  };
};
